import React, { useState, useEffect } from 'react';
import { ClipboardList } from 'lucide-react';
import Modal from './Modal';

export interface AuditRecord {
  audit_id: number;
  action: string;
  changed_by_username: string;
  changed_on: string;
  old_snapshot?: any;
  new_snapshot?: any;
  changed_fields?: string[];
  change_reason?: string;
  admin_comments?: string;
}

interface AuditHistoryModalProps {
  show: boolean;
  onClose: () => void;
  entityId: number;
  entityType: string;
  entityName?: string;
  fetchHistoryFn: (entityId: number) => Promise<AuditRecord[]>;
}

const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({
  show,
  onClose,
  entityId,
  entityName,
  fetchHistoryFn
}) => {
  const [history, setHistory] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRecords, setExpandedRecords] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (show && entityId) {
      loadHistory();
    }
  }, [show, entityId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchHistoryFn(entityId);
      setHistory(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit history');
      console.error('Error loading history:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (auditId: number) => {
    const newExpanded = new Set(expandedRecords);
    if (newExpanded.has(auditId)) {
      newExpanded.delete(auditId);
    } else {
      newExpanded.add(auditId);
    }
    setExpandedRecords(newExpanded);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE':     return '#10b981';
      case 'UPDATE':     return '#3b82f6';
      case 'DELETE':     return '#ef4444';
      case 'ACTIVATE':   return '#22c55e';
      case 'DEACTIVATE': return '#f59e0b';
      case 'RESTORE':    return '#8b5cf6';
      default:           return '#6b7280';
    }
  };

  const renderFieldChanges = (record: AuditRecord) => {
    if (!record.changed_fields || record.changed_fields.length === 0) return null;

    const oldSnapshot = record.old_snapshot || {};
    const newSnapshot = record.new_snapshot || {};

    return (
      <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 6, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>Changed Fields:</div>
        {record.changed_fields.map((field) => (
          <div key={field} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: 500, color: '#6b7280', marginBottom: 4, textTransform: 'capitalize' }}>
              {field.replace(/_/g, ' ')}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <span className="status-badge xs danger" style={{ marginBottom: 4, display: 'inline-block' }}>OLD</span>
                <div style={{ padding: '4px 8px', background: '#fee2e2', borderRadius: 4, color: '#991b1b', fontSize: 12, wordBreak: 'break-word' }}>
                  {oldSnapshot[field] != null ? String(oldSnapshot[field]) : 'NULL'}
                </div>
              </div>
              <span style={{ color: '#9ca3af' }}>→</span>
              <div style={{ flex: 1 }}>
                <span className="status-badge xs success" style={{ marginBottom: 4, display: 'inline-block' }}>NEW</span>
                <div style={{ padding: '4px 8px', background: '#d1fae5', borderRadius: 4, color: '#065f46', fontSize: 12, wordBreak: 'break-word' }}>
                  {newSnapshot[field] != null ? String(newSnapshot[field]) : 'NULL'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal isOpen={show} onClose={onClose} size="lg">
      <Modal.Header>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClipboardList size={18} />
          Audit History
          {entityName && (
            <span style={{ fontWeight: 400, fontSize: '0.875rem', color: '#6b7280' }}>
              — {entityName}
            </span>
          )}
        </span>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            Loading audit history...
          </div>
        ) : error ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <div style={{ color: '#ef4444', marginBottom: 12 }}>{error}</div>
            <button onClick={loadHistory} style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} data-testid="BTN_audit_history_retry">
              Retry
            </button>
          </div>
        ) : history.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            No audit history found for this entity.
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {/* Timeline vertical line */}
            <div style={{ position: 'absolute', left: 20, top: 0, bottom: 0, width: 2, background: '#e5e7eb' }} />

            {history.map((record, index) => (
              <div
                key={record.audit_id}
                style={{ position: 'relative', marginBottom: index === history.length - 1 ? 0 : 24, paddingLeft: 52 }}
              >
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute', left: 12, top: 4,
                  width: 18, height: 18, borderRadius: '50%',
                  background: getActionColor(record.action),
                  border: '3px solid white',
                  boxShadow: `0 0 0 2px ${getActionColor(record.action)}`
                }} />

                {/* Record card */}
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                    <span style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: 4,
                      fontSize: 13, fontWeight: 600,
                      color: getActionColor(record.action),
                      background: getActionColor(record.action) + '15'
                    }}>
                      {record.action}
                    </span>
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#6b7280' }}>
                      <div style={{ fontWeight: 500, color: '#374151' }}>{record.changed_by_username}</div>
                      <div>{formatDate(record.changed_on)}</div>
                    </div>
                  </div>

                  {record.change_reason && (
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 8, fontStyle: 'italic' }}>
                      Reason: {record.change_reason}
                    </div>
                  )}

                  {record.admin_comments && (
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                      Comments: {record.admin_comments}
                    </div>
                  )}

                  {record.changed_fields && record.changed_fields.length > 0 && (
                    <>
                      <button
                        onClick={() => toggleExpanded(record.audit_id)}
                        style={{
                          marginTop: 12, padding: '6px 12px', fontSize: 12,
                          background: '#f3f4f6', border: '1px solid #d1d5db',
                          borderRadius: 4, cursor: 'pointer', color: '#374151', fontWeight: 500
                        }}
                        data-testid={`BTN_audit_history_record_${record.audit_id}_toggle`}
                      >
                        {expandedRecords.has(record.audit_id) ? '▼' : '▶'}{' '}
                        Show {record.changed_fields.length} field{record.changed_fields.length > 1 ? 's' : ''} changed
                      </button>
                      {expandedRecords.has(record.audit_id) && renderFieldChanges(record)}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AuditHistoryModal;
